import { watch } from 'vue';
import L from 'leaflet';
import { storeToRefs } from 'pinia';
import { usePlaceSuggestionsMapStore } from '@/stores/usePlaceSuggestionsMapStore';
import {
  renderSuggestionEntries,
  suggestionEntriesBounds,
  type SuggestionLayerEntry,
} from '@/utils/map/placeSuggestions';

type MapGetter = () => L.Map | null | undefined;

/**
 * Wires the place-suggestions registry to a Leaflet map: draws a numbered pin
 * + polygon per registered suggestion, fit-bounds to the set, and recenters /
 * highlights when a suggestion card is focused. Shared by the Trackers
 * (DeviceMap) and Geofences (GeofenceMap) maps.
 *
 * Call once from a component's `setup()`.
 *
 * @param getMap  Returns the live Leaflet map, or null before it is created.
 *                Must read a reactive source (a `ref`, or a `reactive`
 *                field) so the layer renders once the map becomes available.
 * @param options.shouldFitAll  Gate for the auto fit-to-all-suggestions
 *                behavior — e.g. only on a list/cluster view, so detail views
 *                are not yanked away from the geofence in focus. Focus-driven
 *                recentering always runs. Defaults to always on.
 */
export function usePlaceSuggestionsMapLayer(
  getMap: MapGetter,
  options: { shouldFitAll?: () => boolean } = {},
) {
  const store = usePlaceSuggestionsMapStore();
  const { entries, focusedId } = storeToRefs(store);
  const group = L.layerGroup([]);
  const shouldFitAll = options.shouldFitAll ?? (() => true);

  const render = () => {
    const map = getMap();
    if (!map) return;
    renderSuggestionEntries(group, entries.value, focusedId.value, (id) =>
      store.focus(id),
    );
    const hasEntries = entries.value.length > 0;
    const isOnMap = map.hasLayer(group);
    if (hasEntries && !isOnMap) {
      map.addLayer(group);
    } else if (!hasEntries && isOnMap) {
      map.removeLayer(group);
    }
  };

  const fit = (subset: SuggestionLayerEntry[]) => {
    const map = getMap();
    if (!map) return;
    const bounds = suggestionEntriesBounds(subset);
    if (!bounds || !bounds.isValid()) return;
    map.fitBounds(bounds, { maxZoom: 16, padding: [48, 48] });
  };

  // Initial render once the map is created. Covers a panel that is already
  // open when the map mounts (e.g. navigating from Trackers to Geofences with
  // the suggestions panel left open).
  watch(
    () => !!getMap(),
    (ready) => {
      if (!ready) return;
      render();
      if (entries.value.length && !focusedId.value && shouldFitAll()) {
        fit(entries.value);
      }
    },
    { immediate: true },
  );

  // Redraw + fit-to-all whenever the registered set changes. The signature
  // folds in rank so pin numbers refresh after a sibling card is dismissed.
  watch(
    () => entries.value.map((e) => `${e.id}:${e.rank}`).join(','),
    (signature) => {
      render();
      if (signature && !focusedId.value && shouldFitAll()) {
        fit(entries.value);
      }
    },
  );

  // Recenter + highlight the pin when a suggestion card is clicked.
  watch(
    () => focusedId.value,
    (id) => {
      render();
      if (!id) return;
      const entry = entries.value.find((e) => e.id === id);
      if (entry) fit([entry]);
    },
  );
}
